import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarDays, Clock, AlertTriangle, CheckCircle2, XCircle, Hourglass, Ban, Palmtree, Timer, ChevronRight, Stethoscope, FileText, Inbox, Send, Accessibility, ShieldCheck, LayoutGrid, GraduationCap, Upload, Paperclip, Download, Heart, Vote, Users, Megaphone, Wallet, CalendarHeart } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppHeader } from "@/components/AppHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { DatePicker } from "@/components/DatePicker";
import { TimePicker } from "@/components/TimePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDateIt, fmtDuration } from "@/lib/services";

const STATUS = {
  pending: { label: "In attesa", icon: Hourglass, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Approvata", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rifiutata", icon: XCircle, cls: "bg-rose-50 text-rose-700 border-rose-200" },
  cancelled: { label: "Annullata", icon: Ban, cls: "bg-slate-100 text-slate-600 border-slate-200" },
  ricezione: { label: "In fase di ricezione", icon: Inbox, cls: "bg-sky-50 text-sky-700 border-sky-200" },
  inviata: { label: "Inviata correttamente", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  non_conforme: { label: "Invio non conforme", icon: AlertTriangle, cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

function todayRomeIso() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}
function daysBetween(fromIso, toIso) {
  return Math.round((new Date(toIso + "T00:00:00") - new Date(fromIso + "T00:00:00")) / 86400000);
}

export default function LeaveRequests() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [appalti, setAppalti] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // "ferie" | "permesso" | null
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const emptyFerie = { appalto_id: "", start_date: "", end_date: "", start_time: "", end_time: "", note: "", kind: "full", single: false };
  const emptyPermesso = { appalto_id: "", start_date: "", start_time: "", end_time: "", note: "" };
  const emptyMalattia = { appalto_id: "", start_date: "", end_date: "", puc_number: "", note: "", single: false };
  const emptyL104 = { appalto_id: "", start_date: "", end_date: "", start_time: "", end_time: "", note: "", kind: "full", single: false };
  const [ferie, setFerie] = useState(emptyFerie);
  const [permesso, setPermesso] = useState(emptyPermesso);
  const [malattia, setMalattia] = useState(emptyMalattia);
  const [l104, setL104] = useState(emptyL104);
  const [l104Confirmed, setL104Confirmed] = useState(false);
  const emptyDS = { appalto_id: "", start_date: "", end_date: "", start_time: "", end_time: "", note: "", kind: "full", single: false };
  const [ds, setDS] = useState(emptyDS);
  const [dsConfirmed, setDsConfirmed] = useState(false);
  const [dsFile, setDsFile] = useState(null);
  const emptySpec = { appalto_id: "", start_date: "", end_date: "", start_time: "", end_time: "", note: "", kind: "full", single: false };
  const [lutto, setLutto] = useState(emptySpec);
  const [luttoFile, setLuttoFile] = useState(null);
  const [elett, setElett] = useState(emptySpec);
  const [elettFile, setElettFile] = useState(null);
  const [congedo, setCongedo] = useState(emptySpec);
  const [congedoFile, setCongedoFile] = useState(null);
  const emptySind = { appalto_id: "", start_date: "", end_date: "", start_time: "", end_time: "", note: "", kind: "full", single: false };
  const [sind, setSind] = useState(emptySind);
  const [sindConfirmed, setSindConfirmed] = useState(false);
  const emptyAssemblea = { appalto_id: "", start_date: "", end_date: "", start_time: "", end_time: "", note: "", kind: "hours", single: false };
  const [assemblea, setAssemblea] = useState(emptyAssemblea);
  const emptyPnr = { appalto_id: "", start_date: "", end_date: "", start_time: "", end_time: "", note: "", kind: "full", single: false };
  const [pnr, setPnr] = useState(emptyPnr);
  const [uploadingId, setUploadingId] = useState(null);
  const [docSel, setDocSel] = useState({});
  const [screen, setScreen] = useState("home");

  const monte = user?.monte_ore_settimanale_contrattuale;
  const maxPermessoOre = monte && monte > 0 ? Math.ceil(monte / 10) : null;

  const load = async () => {
    const [a, r, c, cd, cs] = await Promise.all([api.get("/my-appalti"), api.get("/leave/requests"), api.get("/leave/legge104/confirmation"), api.get("/leave/diritto-studio/confirmation"), api.get("/leave/sindacale/confirmation")]);
    setAppalti(a.data || []);
    setRequests(r.data || []);
    setL104Confirmed(!!c.data?.confirmed);
    setDsConfirmed(!!cd.data?.confirmed);
    setSindConfirmed(!!cs.data?.confirmed);
    if ((a.data || []).length === 1) {
      setFerie((f) => ({ ...f, appalto_id: a.data[0].id }));
      setPermesso((f) => ({ ...f, appalto_id: a.data[0].id }));
      setMalattia((f) => ({ ...f, appalto_id: a.data[0].id }));
      setL104((f) => ({ ...f, appalto_id: a.data[0].id }));
      setDS((f) => ({ ...f, appalto_id: a.data[0].id }));
      setSind((f) => ({ ...f, appalto_id: a.data[0].id }));
      setAssemblea((f) => ({ ...f, appalto_id: a.data[0].id }));
      setPnr((f) => ({ ...f, appalto_id: a.data[0].id }));
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // All'apertura, segna come visti gli aggiornamenti dell'Ufficio (azzera il badge in Home).
  useEffect(() => { api.post("/leave/mark-updates-seen", {}).catch(() => {}); }, []);

  const openFerie = () => { setError(""); setFerie((f) => ({ ...emptyFerie, appalto_id: appalti.length === 1 ? appalti[0].id : "" })); setMode("ferie"); };
  const openPermesso = () => { setError(""); setPermesso((f) => ({ ...emptyPermesso, appalto_id: appalti.length === 1 ? appalti[0].id : "" })); setMode("permesso"); };
  const openMalattia = () => { setError(""); setMalattia((f) => ({ ...emptyMalattia, appalto_id: appalti.length === 1 ? appalti[0].id : "" })); setMode("malattia"); };
  const openL104 = () => {
    setError(""); setL104((f) => ({ ...emptyL104, appalto_id: appalti.length === 1 ? appalti[0].id : "" }));
    setMode(l104Confirmed ? "l104" : "l104-confirm");
  };
  const confirmL104 = async () => {
    setSaving(true);
    try {
      await api.post("/leave/legge104/confirm", {});
      setL104Confirmed(true); setMode("l104");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const pucValid = /^[0-9]{8,13}$/.test((malattia.puc_number || "").trim());
  const malMinDate = (() => { const d = new Date(todayRomeIso() + "T00:00:00"); d.setDate(d.getDate() - 2); return new Intl.DateTimeFormat("en-CA").format(d); })();

  const submitMalattia = async (e) => {
    e.preventDefault(); setError("");
    const end = malattia.single ? malattia.start_date : malattia.end_date;
    if (!malattia.appalto_id || !malattia.start_date || !end) { setError("Compila appalto e data/e."); return; }
    if (end < malattia.start_date) { setError("La data fine non può precedere la data inizio."); return; }
    if (malattia.start_date < malMinDate) { setError("La data di inizio non può essere anteriore a 2 giorni fa (retroattività massima 2 giorni)."); return; }
    if (!pucValid) { setError("Il PUK Malattia deve contenere da 8 a 13 cifre."); return; }
    setSaving(true);
    try {
      await api.post("/leave/requests", {
        appalto_id: malattia.appalto_id, causale: "malattia", start_date: malattia.start_date, end_date: end,
        puc_number: malattia.puc_number.trim(), note: malattia.note.trim() || null,
      });
      toast.success("Richiesta di malattia inviata"); setMode(null); await load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const submitFerie = async (e) => {
    e.preventDefault(); setError("");
    if (!ferie.appalto_id) { setError("Seleziona l'appalto."); return; }
    const payload = { appalto_id: ferie.appalto_id, causale: "ferie", note: ferie.note.trim() || null };
    if (ferie.kind === "hours") {
      if (!ferie.start_date || !ferie.start_time || !ferie.end_time) { setError("Indica data e fascia oraria (dalle / alle)."); return; }
      if (ferie.end_time <= ferie.start_time) { setError("L'orario finale deve essere successivo a quello iniziale."); return; }
      payload.start_date = ferie.start_date; payload.start_time = ferie.start_time; payload.end_time = ferie.end_time;
    } else {
      if (!ferie.start_date) { setError("Compila la data."); return; }
      const end = ferie.single ? ferie.start_date : ferie.end_date;
      if (!ferie.single && !ferie.end_date) { setError("Compila data inizio e data fine."); return; }
      if (end < ferie.start_date) { setError("La data fine non può precedere la data inizio."); return; }
      payload.start_date = ferie.start_date; payload.end_date = end;
    }
    setSaving(true);
    try {
      await api.post("/leave/requests", payload);
      toast.success("Richiesta di ferie inviata"); setMode(null); await load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const submitL104 = async (e) => {
    e.preventDefault(); setError("");
    if (!l104.appalto_id) { setError("Seleziona l'appalto."); return; }
    const payload = { appalto_id: l104.appalto_id, causale: "permesso_104", note: l104.note.trim() || null };
    if (l104.kind === "hours") {
      if (!l104.start_date || !l104.start_time || !l104.end_time) { setError("Indica data e fascia oraria (dalle / alle)."); return; }
      if (l104.end_time <= l104.start_time) { setError("L'orario finale deve essere successivo a quello iniziale."); return; }
      payload.start_date = l104.start_date; payload.start_time = l104.start_time; payload.end_time = l104.end_time;
    } else {
      if (!l104.start_date) { setError("Compila la data."); return; }
      const end = l104.single ? l104.start_date : l104.end_date;
      if (!l104.single && !l104.end_date) { setError("Compila data inizio e data fine."); return; }
      if (end < l104.start_date) { setError("La data fine non può precedere la data inizio."); return; }
      payload.start_date = l104.start_date; payload.end_date = end;
    }
    setSaving(true);
    try {
      await api.post("/leave/requests", payload);
      toast.success("Richiesta Permesso Legge 104 inviata"); setMode(null); await load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const openSind = () => {
    setError(""); setSind((f) => ({ ...emptySind, appalto_id: appalti.length === 1 ? appalti[0].id : "" }));
    setMode(sindConfirmed ? "sind" : "sind-confirm");
  };
  const confirmSind = async () => {
    setSaving(true);
    try {
      await api.post("/leave/sindacale/confirm", {});
      setSindConfirmed(true); setMode("sind");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  const submitSind = async (e) => {
    e.preventDefault(); setError("");
    if (!sind.appalto_id) { setError("Seleziona l'appalto."); return; }
    const payload = { appalto_id: sind.appalto_id, causale: "permesso_sindacale", note: sind.note.trim() || null };
    if (sind.kind === "hours") {
      if (!sind.start_date || !sind.start_time || !sind.end_time) { setError("Indica data e fascia oraria (dalle / alle)."); return; }
      if (sind.end_time <= sind.start_time) { setError("L'orario finale deve essere successivo a quello iniziale."); return; }
      payload.start_date = sind.start_date; payload.start_time = sind.start_time; payload.end_time = sind.end_time;
    } else {
      if (!sind.start_date) { setError("Compila la data."); return; }
      const end = sind.single ? sind.start_date : sind.end_date;
      if (!sind.single && !sind.end_date) { setError("Compila data inizio e data fine."); return; }
      if (end < sind.start_date) { setError("La data fine non può precedere la data inizio."); return; }
      payload.start_date = sind.start_date; payload.end_date = end;
    }
    setSaving(true);
    try {
      await api.post("/leave/requests", payload);
      toast.success("Richiesta Permesso sindacale RSU/RSA inviata"); setMode(null); await load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const openAssemblea = () => {
    setError(""); setAssemblea((f) => ({ ...emptyAssemblea, appalto_id: appalti.length === 1 ? appalti[0].id : "" }));
    setMode("assemblea");
  };
  const submitAssemblea = async (e) => {
    e.preventDefault(); setError("");
    if (!assemblea.appalto_id) { setError("Seleziona l'appalto."); return; }
    const payload = { appalto_id: assemblea.appalto_id, causale: "assemblea_sindacale", note: assemblea.note.trim() || null };
    if (assemblea.kind === "hours") {
      if (!assemblea.start_date || !assemblea.start_time || !assemblea.end_time) { setError("Indica data e fascia oraria (dalle / alle)."); return; }
      if (assemblea.end_time <= assemblea.start_time) { setError("L'orario finale deve essere successivo a quello iniziale."); return; }
      payload.start_date = assemblea.start_date; payload.start_time = assemblea.start_time; payload.end_time = assemblea.end_time;
    } else {
      if (!assemblea.start_date) { setError("Compila la data."); return; }
      const end = assemblea.single ? assemblea.start_date : assemblea.end_date;
      if (!assemblea.single && !assemblea.end_date) { setError("Compila data inizio e data fine."); return; }
      if (end < assemblea.start_date) { setError("La data fine non può precedere la data inizio."); return; }
      payload.start_date = assemblea.start_date; payload.end_date = end;
    }
    setSaving(true);
    try {
      await api.post("/leave/requests", payload);
      toast.success("Richiesta Assemblea sindacale inviata"); setMode(null); await load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const openPnr = () => {
    setError(""); setPnr((f) => ({ ...emptyPnr, appalto_id: appalti.length === 1 ? appalti[0].id : "" }));
    setMode("pnr");
  };
  const submitPnr = async (e) => {
    e.preventDefault(); setError("");
    if (!pnr.appalto_id) { setError("Seleziona l'appalto."); return; }
    if (!pnr.note.trim()) { setError("La nota è obbligatoria per il Permesso non retribuito."); return; }
    const payload = { appalto_id: pnr.appalto_id, causale: "permesso_non_retribuito", note: pnr.note.trim() };
    if (pnr.kind === "hours") {
      if (!pnr.start_date || !pnr.start_time || !pnr.end_time) { setError("Indica data e fascia oraria (dalle / alle)."); return; }
      if (pnr.end_time <= pnr.start_time) { setError("L'orario finale deve essere successivo a quello iniziale."); return; }
      payload.start_date = pnr.start_date; payload.start_time = pnr.start_time; payload.end_time = pnr.end_time;
    } else {
      if (!pnr.start_date) { setError("Compila la data."); return; }
      const end = pnr.single ? pnr.start_date : pnr.end_date;
      if (!pnr.single && !pnr.end_date) { setError("Compila data inizio e data fine."); return; }
      if (end < pnr.start_date) { setError("La data fine non può precedere la data inizio."); return; }
      payload.start_date = pnr.start_date; payload.end_date = end;
    }
    setSaving(true);
    try {
      await api.post("/leave/requests", payload);
      toast.success("Richiesta Permesso non retribuito inviata"); setMode(null); await load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const openDS = () => {
    setError(""); setDsFile(null); setDS((f) => ({ ...emptyDS, appalto_id: appalti.length === 1 ? appalti[0].id : "" }));
    setMode(dsConfirmed ? "ds" : "ds-confirm");
  };
  const confirmDS = async () => {
    setSaving(true);
    try { await api.post("/leave/diritto-studio/confirm", {}); setDsConfirmed(true); setMode("ds"); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  const uploadDoc = async (reqId, file) => {
    if (!file) return;
    setUploadingId(reqId);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/leave/requests/${reqId}/document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documentazione inviata"); setDocSel((s) => { const n = { ...s }; delete n[reqId]; return n; }); await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setUploadingId(null); }
  };
  const submitDS = async (e) => {
    e.preventDefault(); setError("");
    if (!ds.appalto_id) { setError("Seleziona l'appalto."); return; }
    const payload = { appalto_id: ds.appalto_id, causale: "diritto_studio", note: ds.note.trim() || null };
    if (ds.kind === "hours") {
      if (!ds.start_date || !ds.start_time || !ds.end_time) { setError("Indica data e fascia oraria (dalle / alle)."); return; }
      if (ds.end_time <= ds.start_time) { setError("L'orario finale deve essere successivo a quello iniziale."); return; }
      payload.start_date = ds.start_date; payload.start_time = ds.start_time; payload.end_time = ds.end_time;
    } else {
      if (!ds.start_date) { setError("Compila la data."); return; }
      const end = ds.single ? ds.start_date : ds.end_date;
      if (!ds.single && !ds.end_date) { setError("Compila data inizio e data fine."); return; }
      if (end < ds.start_date) { setError("La data fine non può precedere la data inizio."); return; }
      payload.start_date = ds.start_date; payload.end_date = end;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/leave/requests", payload);
      if (dsFile && data?.id) {
        const fd = new FormData(); fd.append("file", dsFile);
        try { await api.post(`/leave/requests/${data.id}/document`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
        catch (e2) { toast.error("Richiesta inviata, ma il caricamento del documento è fallito. Riprova da 'Le tue richieste'."); }
      }
      toast.success("Richiesta Diritto allo studio inviata"); setMode(null); setDsFile(null); await load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const openLutto = () => { setError(""); setLuttoFile(null); setLutto({ ...emptySpec, appalto_id: appalti.length === 1 ? appalti[0].id : "" }); setMode("lutto"); };
  const openElett = () => { setError(""); setElettFile(null); setElett({ ...emptySpec, appalto_id: appalti.length === 1 ? appalti[0].id : "" }); setMode("elett"); };
  const openCongedo = () => { setError(""); setCongedoFile(null); setCongedo({ ...emptySpec, appalto_id: appalti.length === 1 ? appalti[0].id : "" }); setMode("congedo"); };
  const submitSpec = async (e, causale, form, file, successMsg) => {
    e.preventDefault(); setError("");
    if (!form.appalto_id) { setError("Seleziona l'appalto."); return; }
    const payload = { appalto_id: form.appalto_id, causale, note: form.note.trim() || null };
    if (form.kind === "hours") {
      if (!form.start_date || !form.start_time || !form.end_time) { setError("Indica data e fascia oraria (dalle / alle)."); return; }
      if (form.end_time <= form.start_time) { setError("L'orario finale deve essere successivo a quello iniziale."); return; }
      payload.start_date = form.start_date; payload.start_time = form.start_time; payload.end_time = form.end_time;
    } else {
      if (!form.start_date) { setError("Compila la data."); return; }
      const end = form.single ? form.start_date : form.end_date;
      if (!form.single && !form.end_date) { setError("Compila data inizio e data fine."); return; }
      if (end < form.start_date) { setError("La data fine non può precedere la data inizio."); return; }
      payload.start_date = form.start_date; payload.end_date = end;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/leave/requests", payload);
      if (file && data?.id) {
        const fd = new FormData(); fd.append("file", file);
        try { await api.post(`/leave/requests/${data.id}/document`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
        catch (e2) { toast.error("Richiesta inviata, ma il caricamento del documento è fallito. Riprova da 'Le tue richieste'."); }
      }
      toast.success(successMsg); setMode(null); await load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const toMin = (t) => { if (!t) return null; const [h, m] = t.split(":").map(Number); return h * 60 + m; };
  const permMaxMin = maxPermessoOre != null ? maxPermessoOre * 60 : null;
  const permDurMin = (permesso.start_time && permesso.end_time) ? (toMin(permesso.end_time) - toMin(permesso.start_time)) : null;
  const permRangeInvalid = permDurMin != null && permDurMin <= 0;
  const permessoOver = permMaxMin != null && permDurMin != null && permDurMin > permMaxMin;

  const submitPermesso = async (e) => {
    e.preventDefault(); setError("");
    if (!permesso.appalto_id || !permesso.start_date) { setError("Compila appalto e data."); return; }
    if (!permesso.start_time || !permesso.end_time) { setError("Indica la fascia oraria (dalle / alle)."); return; }
    if (permRangeInvalid) { setError("L'orario finale deve essere successivo a quello iniziale."); return; }
    if (permessoOver) { setError(`Massimo richiedibile: ${maxPermessoOre} ore al giorno.`); return; }
    setSaving(true);
    try {
      await api.post("/leave/requests", {
        appalto_id: permesso.appalto_id, causale: "permesso", start_date: permesso.start_date,
        start_time: permesso.start_time, end_time: permesso.end_time, note: permesso.note.trim() || null,
      });
      toast.success("Richiesta di permesso inviata"); setMode(null); await load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const feriePreavvisoShort = ferie.start_date && daysBetween(todayRomeIso(), ferie.start_date) < 7;

  return (
    <div className="worker-theme min-h-screen bg-[#EDF0F4] pb-10">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 py-6">
        {screen === "speciali" ? (
          <button data-testid="speciali-back-btn" onClick={() => { setError(""); setScreen("home"); }}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline mb-4">
            <ArrowLeft className="h-4 w-4" /> Assenze e permessi
          </button>
        ) : (
          <button data-testid="leave-back-btn" onClick={() => navigate("/lavoratore")}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline mb-4">
            <ArrowLeft className="h-4 w-4" /> Torna alla home
          </button>
        )}

        {screen === "speciali" ? (
          <>
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] flex items-center justify-center shadow-inner shrink-0"><LayoutGrid className="h-5 w-5 text-[#22364D]" /></div>
              <div>
                <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Permessi speciali</h1>
                <p className="text-sm text-slate-500 mt-0.5">Accedi alle diverse tipologie di permesso</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6" data-testid="permessi-speciali-screen">
              <button data-testid="leave-card-legge104" onClick={openL104}
                className="group text-left rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-200 p-4">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] shadow-inner flex items-center justify-center mb-3"><Accessibility className="h-5 w-5 text-[#22364D]" /></div>
                <p className="font-heading font-bold text-slate-800 leading-tight">Permesso Legge 104</p>
                <span className="mt-2 inline-flex items-center text-xs font-semibold text-primary">Richiedi <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
              </button>
              <button data-testid="leave-card-diritto-studio" onClick={openDS}
                className="group text-left rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-200 p-4">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] shadow-inner flex items-center justify-center mb-3"><GraduationCap className="h-5 w-5 text-[#22364D]" /></div>
                <p className="font-heading font-bold text-slate-800 leading-tight">Diritto allo studio</p>
                <span className="mt-2 inline-flex items-center text-xs font-semibold text-primary">Richiedi <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
              </button>
              <button data-testid="leave-card-lutto" onClick={openLutto}
                className="group text-left rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-200 p-4">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] shadow-inner flex items-center justify-center mb-3"><Heart className="h-5 w-5 text-[#22364D]" /></div>
                <p className="font-heading font-bold text-slate-800 leading-tight">Permesso per lutto</p>
                <span className="mt-2 inline-flex items-center text-xs font-semibold text-primary">Richiedi <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
              </button>
              <button data-testid="leave-card-elettorale" onClick={openElett}
                className="group text-left rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-200 p-4">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] shadow-inner flex items-center justify-center mb-3"><Vote className="h-5 w-5 text-[#22364D]" /></div>
                <p className="font-heading font-bold text-slate-800 leading-tight">Permesso per funzioni elettorali</p>
                <span className="mt-2 inline-flex items-center text-xs font-semibold text-primary">Richiedi <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
              </button>
              <button data-testid="leave-card-sindacale" onClick={openSind}
                className="group text-left rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-200 p-4">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] shadow-inner flex items-center justify-center mb-3"><Users className="h-5 w-5 text-[#22364D]" /></div>
                <p className="font-heading font-bold text-slate-800 leading-tight">Permesso sindacale RSU/RSA</p>
                <span className="mt-2 inline-flex items-center text-xs font-semibold text-primary">Richiedi <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
              </button>
              <button data-testid="leave-card-assemblea" onClick={openAssemblea}
                className="group text-left rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-200 p-4">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] shadow-inner flex items-center justify-center mb-3"><Megaphone className="h-5 w-5 text-[#22364D]" /></div>
                <p className="font-heading font-bold text-slate-800 leading-tight">Assemblea sindacale</p>
                <span className="mt-2 inline-flex items-center text-xs font-semibold text-primary">Richiedi <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
              </button>
              <button data-testid="leave-card-pnr" onClick={openPnr}
                className="group text-left rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-200 p-4">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] shadow-inner flex items-center justify-center mb-3"><Wallet className="h-5 w-5 text-[#22364D]" /></div>
                <p className="font-heading font-bold text-slate-800 leading-tight">Permesso non retribuito</p>
                <span className="mt-2 inline-flex items-center text-xs font-semibold text-primary">Richiedi <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
              </button>
              <button data-testid="leave-card-congedo" onClick={openCongedo}
                className="group text-left rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-200 p-4">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] shadow-inner flex items-center justify-center mb-3"><CalendarHeart className="h-5 w-5 text-[#22364D]" /></div>
                <p className="font-heading font-bold text-slate-800 leading-tight">Congedo</p>
                <span className="mt-2 inline-flex items-center text-xs font-semibold text-primary">Richiedi <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
              </button>
            </div>
          </>
        ) : (
        <>
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] flex items-center justify-center shadow-inner shrink-0"><CalendarDays className="h-5 w-5 text-[#22364D]" /></div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Assenze e permessi</h1>
            <p className="text-sm text-slate-500 mt-0.5">Invia una richiesta e consulta lo stato</p>
          </div>
        </div>

        <h2 className="orbit-area-title font-heading text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2"><span className="inline-block h-4 w-1.5 rounded-full bg-[#C6A3B4]" />Nuova richiesta</h2>
        <div className="grid grid-cols-2 gap-3">
          <button data-testid="leave-card-ferie" onClick={openFerie}
            className="group text-left rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-200 p-4">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] shadow-inner flex items-center justify-center mb-3"><Palmtree className="h-5 w-5 text-[#22364D]" /></div>
            <p className="font-heading font-bold text-slate-800 leading-tight">Ferie</p>
            <span className="mt-2 inline-flex items-center text-xs font-semibold text-primary">Richiedi <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
          </button>
          <button data-testid="leave-card-permesso" onClick={openPermesso}
            className="group text-left rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-200 p-4">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] shadow-inner flex items-center justify-center mb-3"><Timer className="h-5 w-5 text-[#22364D]" /></div>
            <p className="font-heading font-bold text-slate-800 leading-tight">Permessi retribuiti</p>
            <span className="mt-2 inline-flex items-center text-xs font-semibold text-primary">Richiedi <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
          </button>
          <button data-testid="leave-card-malattia" onClick={openMalattia}
            className="group text-left rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-200 p-4">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] shadow-inner flex items-center justify-center mb-3"><Stethoscope className="h-5 w-5 text-[#22364D]" /></div>
            <p className="font-heading font-bold text-slate-800 leading-tight">Malattia</p>
            <span className="mt-2 inline-flex items-center text-xs font-semibold text-primary">Comunica <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
          </button>
          <button data-testid="leave-card-permessi-speciali" onClick={() => { setError(""); setScreen("speciali"); }}
            className="group text-left rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-200 p-4">
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] shadow-inner flex items-center justify-center mb-3"><LayoutGrid className="h-5 w-5 text-[#22364D]" /></div>
            <p className="font-heading font-bold text-slate-800 leading-tight">Permessi speciali</p>
            <span className="mt-2 inline-flex items-center text-xs font-semibold text-primary">Accedi alle tipologie <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
          </button>
        </div>

        <h2 className="orbit-area-title font-heading text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2"><span className="inline-block h-4 w-1.5 rounded-full bg-[#C6A3B4]" />Le tue richieste</h2>
        <div className="space-y-3" data-testid="leave-requests-list">
          {loading ? (
            [0, 1].map((i) => <div key={i} className="rounded-xl bg-white border border-slate-200 h-24 animate-pulse" />)
          ) : requests.length === 0 ? (
            <Card className="rounded-xl border-slate-200 shadow-sm">
              <CardContent className="p-8 text-center text-slate-500 text-sm" data-testid="leave-empty">Non hai ancora inviato richieste.</CardContent>
            </Card>
          ) : (
            requests.map((r, i) => {
              const st = STATUS[r.status] || STATUS.pending;
              const StIcon = st.icon;
              const isPermesso = r.causale === "permesso";
              const isMalattia = r.causale === "malattia";
              const is104 = r.causale === "permesso_104";
              const isDS = r.causale === "diritto_studio";
              const isSind = r.causale === "permesso_sindacale";
              const isAssemblea = r.causale === "assemblea_sindacale";
              const isPnr = r.causale === "permesso_non_retribuito";
              const isDoc = ["diritto_studio", "permesso_lutto", "permesso_elettorale", "congedo"].includes(r.causale);
              const specIcon = r.causale === "permesso_lutto" ? <Heart className="h-4 w-4 text-[#557A95]" /> : r.causale === "congedo" ? <CalendarHeart className="h-4 w-4 text-[#2E6B6B]" /> : r.causale === "permesso_elettorale" ? <Vote className="h-4 w-4 text-[#A97C8C]" /> : <GraduationCap className="h-4 w-4 text-[#2F5C7D]" />;
              const hasTime = !!(r.start_time && r.end_time);
              const singleLine = isPermesso || ((is104 || isSind || isAssemblea || isPnr || isDoc) && hasTime);
              const DS_BADGE = { da_caricare: "bg-slate-100 text-slate-600 border-slate-200", caricata: "bg-sky-50 text-sky-700 border-sky-200", verificata: "bg-emerald-50 text-emerald-700 border-emerald-200", non_conforme: "bg-rose-50 text-rose-700 border-rose-200", mancante: "bg-amber-50 text-amber-700 border-amber-200" };
              let topBadge = { label: st.label, cls: st.cls, Icon: StIcon };
              if (isDoc && r.status === "approved") {
                if (r.doc_status === "verificata") topBadge = { label: "Approvata", cls: STATUS.approved.cls, Icon: CheckCircle2 };
                else if (r.doc_status === "caricata") topBadge = { label: "Documentazione in verifica", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Hourglass };
                else if (r.doc_status === "non_conforme") topBadge = { label: "Documentazione non conforme", cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: AlertTriangle };
                else topBadge = { label: "In attesa di documentazione", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Hourglass };
              }
              return (
                <motion.div key={r.id} data-testid={`leave-card-${i}`}
                  initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: Math.min(i * 0.04, 0.3) }}
                  className="rounded-xl bg-white border border-slate-200 shadow-sm p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 font-semibold text-slate-800">
                      {isMalattia ? <Stethoscope className="h-4 w-4 text-rose-600" /> : is104 ? <Accessibility className="h-4 w-4 text-emerald-700" /> : isSind ? <Users className="h-4 w-4 text-[#3E6470]" /> : isAssemblea ? <Megaphone className="h-4 w-4 text-[#3D5A6C]" /> : isPnr ? <Wallet className="h-4 w-4 text-[#22364D]" /> : isDoc ? specIcon : isPermesso ? <Timer className="h-4 w-4 text-primary" /> : <Palmtree className="h-4 w-4 text-emerald-600" />}
                      <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{r.causale_label}</span>
                    </div>
                    <Badge className={`border ${topBadge.cls} gap-1`} data-testid={`leave-status-${i}`}><topBadge.Icon className="h-3.5 w-3.5" /> {topBadge.label}</Badge>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-slate-800 font-semibold">
                    <CalendarDays className="h-4 w-4 text-primary" />
                    {singleLine ? `${fmtDateIt(r.start_date)}${r.start_time ? ` · ${r.start_time}–${r.end_time}` : ""}` : `${fmtDateIt(r.start_date)} → ${fmtDateIt(r.end_date)}`}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span>{r.appalto_nome}</span>
                    {isMalattia ? (
                      <span className="inline-flex items-center gap-1" data-testid={`leave-puc-${i}`}><FileText className="h-3.5 w-3.5" /> PUC {r.puc_number}</span>
                    ) : r.full_day ? (
                      <span className="inline-flex items-center gap-1 font-semibold text-slate-600" data-testid={`leave-fullday-${i}`}><CalendarDays className="h-3.5 w-3.5" /> Giornata intera</span>
                    ) : (
                      <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {fmtDuration(r.computed_total_minutes || 0)}</span>
                    )}
                  </div>
                  {r.note && <p className="mt-2 text-sm text-slate-600">{r.note}</p>}
                  {r.status === "pending" && !r.preavviso_ok && (
                    <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1.5" data-testid={`leave-preavviso-warn-${i}`}>
                      <AlertTriangle className="h-3.5 w-3.5" /> Inviata con meno di 7 giorni di preavviso
                    </p>
                  )}
                  {r.review_note && (r.status === "rejected" || r.status === "approved") && (
                    <p className={`mt-2 text-xs rounded-lg px-2.5 py-1.5 ${r.status === "rejected" ? "text-rose-700 bg-rose-50 border border-rose-200" : "text-slate-600 bg-slate-50 border border-slate-200"}`} data-testid={`leave-review-note-${i}`}>
                      <b>Motivazione Ufficio:</b> {r.review_note}
                    </p>
                  )}
                  {r.status === "cancelled" && (
                    <p className="mt-2 text-xs text-slate-500" data-testid={`leave-cancelled-${i}`}>Annullata dall'Ufficio{r.cancelled_by ? ` · ${r.cancelled_by}` : ""}</p>
                  )}
                  {r.status === "approved" && (r.over_8h_days || []).length > 0 && (
                    <p className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1.5" data-testid={`leave-over8-${i}`}>
                      <AlertTriangle className="h-3.5 w-3.5" /> Superate 8 ore complessive in {r.over_8h_days.length} {r.over_8h_days.length === 1 ? "giornata" : "giornate"}
                    </p>
                  )}
                  {isDoc && r.status === "approved" && (
                    <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`ds-doc-panel-${i}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Documentazione</span>
                        <Badge className={`border ${DS_BADGE[r.doc_status] || DS_BADGE.da_caricare}`} data-testid={`ds-doc-status-${i}`}>{r.doc_status_label}</Badge>
                      </div>
                      {r.has_document && (
                        <a href="#" onClick={async (e) => { e.preventDefault(); try { const res = await api.get(`/leave/requests/${r.id}/document`, { responseType: "blob" }); const u = URL.createObjectURL(res.data); window.open(u, "_blank"); } catch { toast.error("Impossibile aprire il documento"); } }}
                          className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline" data-testid={`ds-doc-view-${i}`}>
                          <FileText className="h-4 w-4" /> {r.doc_filename || "Visualizza documento"}
                        </a>
                      )}
                      {r.doc_transmitted && (
                        <p className="mt-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5 inline-flex items-start gap-1.5" data-testid={`ds-doc-transmitted-${i}`}>
                          <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" /> Documentazione trasmessa — Il documento è stato trasmesso allo Spazio Giustificativi e non è più conservato su Orbit.
                        </p>
                      )}
                      {r.doc_status === "non_conforme" && r.doc_review_note && (
                        <p className="mt-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-1.5" data-testid={`ds-doc-note-${i}`}>
                          <b>Ufficio:</b> {r.doc_review_note}
                        </p>
                      )}
                      {r.doc_status === "mancante" && (
                        <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5" data-testid={`ds-doc-missing-${i}`}>
                          Non risulta ancora caricata la documentazione relativa al Diritto allo studio. Contatta il tuo coordinatore di riferimento per chiarire la situazione e individuare una soluzione.
                        </p>
                      )}
                      {r.doc_status !== "verificata" && (
                        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`ds-doc-upload-wrap-${i}`}>
                          <label className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 cursor-pointer hover:bg-slate-50 text-sm font-semibold text-slate-700" data-testid={`ds-doc-upload-label-${i}`}>
                            <Paperclip className="h-4 w-4" /> {docSel[r.id] ? docSel[r.id].name : (r.has_document ? "Sostituisci documentazione" : "Carica documentazione")}
                            <input type="file" accept=".pdf,image/*" className="hidden" disabled={uploadingId === r.id} data-testid={`ds-doc-upload-input-${i}`}
                              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) setDocSel((s) => ({ ...s, [r.id]: f })); }} />
                          </label>
                          {docSel[r.id] && (
                            <Button size="sm" disabled={uploadingId === r.id} onClick={() => uploadDoc(r.id, docSel[r.id])} data-testid={`ds-doc-send-${i}`} className="bg-[#2F5C7D] hover:bg-[#264c68]">
                              <Upload className="h-4 w-4 mr-1.5" /> {uploadingId === r.id ? "Invio…" : "Invia documentazione"}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              );
            })
          )}
        </div>
        </>
        )}

        {/* Dialog Ferie */}
        <Dialog open={mode === "ferie"} onOpenChange={(o) => !o && setMode(null)}>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle className="font-heading">Nuova richiesta di ferie</DialogTitle></DialogHeader>
            <form onSubmit={submitFerie} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Appalto</Label>
                <Select value={ferie.appalto_id} onValueChange={(v) => setFerie((f) => ({ ...f, appalto_id: v }))}>
                  <SelectTrigger data-testid="ferie-appalto-select" className="h-12"><SelectValue placeholder="Seleziona l'appalto" /></SelectTrigger>
                  <SelectContent>{appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="ferie-kind-full" onClick={() => setFerie((f) => ({ ...f, kind: "full" }))}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${ferie.kind === "full" ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-600"}`}>Giornata intera</button>
                <button type="button" data-testid="ferie-kind-hours" onClick={() => setFerie((f) => ({ ...f, kind: "hours" }))}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${ferie.kind === "hours" ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-600"}`}>A ore (dalle / alle)</button>
              </div>
              {ferie.kind === "full" ? (
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer" data-testid="ferie-single-toggle">
                    <input type="checkbox" checked={ferie.single} onChange={(e) => setFerie((f) => ({ ...f, single: e.target.checked }))} /> Giornata singola
                  </label>
                  {ferie.single ? (
                    <DatePicker label="Data" value={ferie.start_date} onChange={(v) => setFerie((f) => ({ ...f, start_date: v }))} testid="ferie-start-date" />
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <DatePicker label="Data inizio" value={ferie.start_date} onChange={(v) => setFerie((f) => ({ ...f, start_date: v }))} testid="ferie-start-date" />
                      <DatePicker label="Data fine" value={ferie.end_date} onChange={(v) => setFerie((f) => ({ ...f, end_date: v }))} testid="ferie-end-date" />
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <DatePicker label="Data" value={ferie.start_date} onChange={(v) => setFerie((f) => ({ ...f, start_date: v }))} testid="ferie-start-date" />
                  <div className="grid grid-cols-2 gap-3">
                    <TimePicker label="Dalle ore" value={ferie.start_time} onChange={(v) => setFerie((f) => ({ ...f, start_time: v }))} testid="ferie-start-time" />
                    <TimePicker label="Alle ore" value={ferie.end_time} onChange={(v) => setFerie((f) => ({ ...f, end_time: v }))} testid="ferie-end-time" />
                  </div>
                </>
              )}
              {ferie.kind === "full" && (
                <p className="text-xs text-slate-500" data-testid="ferie-fullday-hint">Giornata/e intera/e: nel Libro Presenze verrà mostrata la sigla <b>F</b>, senza attribuzione automatica di ore.</p>
              )}
              {feriePreavvisoShort && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-flex items-center gap-1.5" data-testid="ferie-preavviso-hint">
                  <AlertTriangle className="h-4 w-4" /> Preavviso inferiore a 7 giorni: la richiesta verrà inviata comunque.
                </p>
              )}
              <div className="space-y-1.5"><Label>Note (opzionale)</Label><Textarea data-testid="ferie-note-input" value={ferie.note} onChange={(e) => setFerie((f) => ({ ...f, note: e.target.value }))} rows={3} /></div>
              {error && <p className="text-sm text-destructive" data-testid="leave-error">{error}</p>}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setMode(null)}>Annulla</Button>
                <Button type="submit" disabled={saving} data-testid="ferie-submit-btn">{saving ? "Invio…" : "Invia richiesta"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Permesso */}
        <Dialog open={mode === "permesso"} onOpenChange={(o) => !o && setMode(null)}>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle className="font-heading">Nuovo permesso retribuito</DialogTitle></DialogHeader>
            <form onSubmit={submitPermesso} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Appalto</Label>
                <Select value={permesso.appalto_id} onValueChange={(v) => setPermesso((f) => ({ ...f, appalto_id: v }))}>
                  <SelectTrigger data-testid="permesso-appalto-select" className="h-12"><SelectValue placeholder="Seleziona l'appalto" /></SelectTrigger>
                  <SelectContent>{appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <DatePicker label="Data" value={permesso.start_date} onChange={(v) => setPermesso((f) => ({ ...f, start_date: v }))} testid="permesso-date" />
              <div className="grid grid-cols-2 gap-3">
                <TimePicker label="Dalle ore" value={permesso.start_time} onChange={(v) => setPermesso((f) => ({ ...f, start_time: v }))} testid="permesso-start-time" />
                <TimePicker label="Alle ore" value={permesso.end_time} onChange={(v) => setPermesso((f) => ({ ...f, end_time: v }))} testid="permesso-end-time" />
              </div>
              <div className="space-y-1">
                {permDurMin != null && !permRangeInvalid && (
                  <p className="text-sm font-semibold text-slate-800" data-testid="permesso-duration">Ore richieste: {fmtDuration(permDurMin)}</p>
                )}
                {permRangeInvalid && <p className="text-xs text-destructive" data-testid="permesso-range-warn">L'orario finale deve essere successivo a quello iniziale.</p>}
                <p className="text-xs text-slate-500" data-testid="permesso-max-hint">
                  {maxPermessoOre != null ? `Massimo richiedibile: ${maxPermessoOre} ${maxPermessoOre === 1 ? "ora" : "ore"}` : "Monte ore non impostato: contatta l'Ufficio"}
                </p>
                {permessoOver && <p className="text-xs text-destructive" data-testid="permesso-over-warn">Superi il massimo richiedibile di {maxPermessoOre} ore.</p>}
              </div>
              <div className="space-y-1.5"><Label>Note (opzionale)</Label><Textarea data-testid="permesso-note-input" value={permesso.note} onChange={(e) => setPermesso((f) => ({ ...f, note: e.target.value }))} rows={3} /></div>
              {error && <p className="text-sm text-destructive" data-testid="leave-error">{error}</p>}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setMode(null)}>Annulla</Button>
                <Button type="submit" disabled={saving || permessoOver || permRangeInvalid} data-testid="permesso-submit-btn">{saving ? "Invio…" : "Invia richiesta"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Malattia */}
        <Dialog open={mode === "malattia"} onOpenChange={(o) => !o && setMode(null)}>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle className="font-heading">Comunica una malattia</DialogTitle></DialogHeader>
            <form onSubmit={submitMalattia} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Appalto</Label>
                <Select value={malattia.appalto_id} onValueChange={(v) => setMalattia((f) => ({ ...f, appalto_id: v }))}>
                  <SelectTrigger data-testid="malattia-appalto-select" className="h-12"><SelectValue placeholder="Seleziona l'appalto" /></SelectTrigger>
                  <SelectContent>{appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer" data-testid="malattia-single-toggle">
                <input type="checkbox" checked={malattia.single} onChange={(e) => setMalattia((f) => ({ ...f, single: e.target.checked }))} /> Giornata singola
              </label>
              {malattia.single ? (
                <DatePicker label="Data" value={malattia.start_date} onChange={(v) => setMalattia((f) => ({ ...f, start_date: v }))} testid="malattia-start-date" />
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <DatePicker label="Dal" value={malattia.start_date} onChange={(v) => setMalattia((f) => ({ ...f, start_date: v }))} testid="malattia-start-date" />
                  <DatePicker label="Al" value={malattia.end_date} onChange={(v) => setMalattia((f) => ({ ...f, end_date: v }))} testid="malattia-end-date" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Numero protocollo PUC <span className="text-destructive">*</span></Label>
                <Input data-testid="malattia-puc-input" value={malattia.puc_number}
                  onChange={(e) => setMalattia((f) => ({ ...f, puc_number: e.target.value }))}
                  placeholder="Da 8 a 13 cifre" className="h-12" maxLength={13} inputMode="numeric" />
                {malattia.puc_number && !pucValid && (
                  <p className="text-xs text-destructive" data-testid="malattia-puc-warn">Il PUK Malattia deve contenere da 8 a 13 cifre.</p>
                )}
              </div>
              <p className="text-xs text-slate-500" data-testid="malattia-retro-hint">Puoi comunicare la malattia con una retroattività massima di 2 giorni.</p>
              <div className="space-y-1.5"><Label>Note (opzionale)</Label><Textarea data-testid="malattia-note-input" value={malattia.note} onChange={(e) => setMalattia((f) => ({ ...f, note: e.target.value }))} rows={3} /></div>
              {error && <p className="text-sm text-destructive" data-testid="leave-error">{error}</p>}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setMode(null)}>Annulla</Button>
                <Button type="submit" disabled={saving || (malattia.puc_number !== "" && !pucValid)} data-testid="malattia-submit-btn">{saving ? "Invio…" : "Invia comunicazione"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog conferma Legge 104 (una tantum) */}
        <Dialog open={mode === "l104-confirm"} onOpenChange={(o) => !o && setMode(null)}>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle className="font-heading">Prima di utilizzare il Permesso Legge 104</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="rounded-xl bg-[#EAF2E8] border border-emerald-200 px-4 py-3 flex items-start gap-2.5" data-testid="l104-confirm-text">
                <ShieldCheck className="h-5 w-5 text-[#22364D] mt-0.5 shrink-0" />
                <p className="text-sm text-slate-700 leading-snug">Confermo di aver già contattato l'Ufficio Personale e di aver già consegnato tutta la documentazione necessaria per usufruire del Permesso Legge 104.</p>
              </div>
              {error && <p className="text-sm text-destructive" data-testid="leave-error">{error}</p>}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setMode(null)}>Annulla</Button>
                <Button type="button" disabled={saving} onClick={confirmL104} data-testid="l104-confirm-btn" className="bg-emerald-600 hover:bg-emerald-700">
                  {saving ? "Attendere…" : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Confermo</>}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog Permesso Legge 104 */}
        <Dialog open={mode === "l104"} onOpenChange={(o) => !o && setMode(null)}>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle className="font-heading">Richiesta Permesso Legge 104</DialogTitle></DialogHeader>
            <form onSubmit={submitL104} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Appalto</Label>
                <Select value={l104.appalto_id} onValueChange={(v) => setL104((f) => ({ ...f, appalto_id: v }))}>
                  <SelectTrigger data-testid="l104-appalto-select" className="h-12"><SelectValue placeholder="Seleziona l'appalto" /></SelectTrigger>
                  <SelectContent>{appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="l104-kind-full" onClick={() => setL104((f) => ({ ...f, kind: "full" }))}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${l104.kind === "full" ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-600"}`}>Giornata intera</button>
                <button type="button" data-testid="l104-kind-hours" onClick={() => setL104((f) => ({ ...f, kind: "hours" }))}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${l104.kind === "hours" ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-600"}`}>A ore (dalle / alle)</button>
              </div>
              {l104.kind === "full" ? (
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer" data-testid="l104-single-toggle">
                    <input type="checkbox" checked={l104.single} onChange={(e) => setL104((f) => ({ ...f, single: e.target.checked }))} /> Giornata singola
                  </label>
                  {l104.single ? (
                    <DatePicker label="Data" value={l104.start_date} onChange={(v) => setL104((f) => ({ ...f, start_date: v }))} testid="l104-start-date" />
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <DatePicker label="Dal" value={l104.start_date} onChange={(v) => setL104((f) => ({ ...f, start_date: v }))} testid="l104-start-date" />
                      <DatePicker label="Al" value={l104.end_date} onChange={(v) => setL104((f) => ({ ...f, end_date: v }))} testid="l104-end-date" />
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <DatePicker label="Data" value={l104.start_date} onChange={(v) => setL104((f) => ({ ...f, start_date: v }))} testid="l104-start-date" />
                  <div className="grid grid-cols-2 gap-3">
                    <TimePicker label="Dalle ore" value={l104.start_time} onChange={(v) => setL104((f) => ({ ...f, start_time: v }))} testid="l104-start-time" />
                    <TimePicker label="Alle ore" value={l104.end_time} onChange={(v) => setL104((f) => ({ ...f, end_time: v }))} testid="l104-end-time" />
                  </div>
                </>
              )}
              <p className="text-xs text-slate-500" data-testid="l104-hint">
                {l104.kind === "full" ? "Giornata/e intera/e: nel Libro Presenze verrà mostrata la sigla PS, senza attribuzione automatica di ore." : "Le ore verranno calcolate automaticamente dalla fascia oraria."}
              </p>
              <div className="space-y-1.5"><Label>Note (opzionale)</Label><Textarea data-testid="l104-note-input" value={l104.note} onChange={(e) => setL104((f) => ({ ...f, note: e.target.value }))} rows={3} /></div>
              {error && <p className="text-sm text-destructive" data-testid="leave-error">{error}</p>}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setMode(null)}>Annulla</Button>
                <Button type="submit" disabled={saving} data-testid="l104-submit-btn">{saving ? "Invio…" : "Invia richiesta"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Permesso non retribuito (PNR) */}
        <Dialog open={mode === "pnr"} onOpenChange={(o) => !o && setMode(null)}>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle className="font-heading">Richiesta Permesso non retribuito</DialogTitle></DialogHeader>
            <form onSubmit={submitPnr} className="space-y-4">
              <div className="rounded-xl bg-[#EDF0F4] border border-[#C6CFD6] px-4 py-3 flex items-start gap-2.5" data-testid="pnr-info-notice">
                <AlertTriangle className="h-5 w-5 text-[#22364D] mt-0.5 shrink-0" />
                <p className="text-sm text-slate-700 leading-snug">Il Permesso non retribuito è soggetto a valutazione dell'Ufficio in relazione alle esigenze organizzative e alla necessità di garantire la regolare continuità del servizio. La richiesta potrà pertanto essere accolta compatibilmente con le esigenze del servizio.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Appalto</Label>
                <Select value={pnr.appalto_id} onValueChange={(v) => setPnr((f) => ({ ...f, appalto_id: v }))}>
                  <SelectTrigger data-testid="pnr-appalto-select" className="h-12"><SelectValue placeholder="Seleziona l'appalto" /></SelectTrigger>
                  <SelectContent>{appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="pnr-kind-full" onClick={() => setPnr((f) => ({ ...f, kind: "full" }))}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${pnr.kind === "full" ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-600"}`}>Giornata intera</button>
                <button type="button" data-testid="pnr-kind-hours" onClick={() => setPnr((f) => ({ ...f, kind: "hours" }))}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${pnr.kind === "hours" ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-600"}`}>A ore (dalle / alle)</button>
              </div>
              {pnr.kind === "full" ? (
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer" data-testid="pnr-single-toggle">
                    <input type="checkbox" checked={pnr.single} onChange={(e) => setPnr((f) => ({ ...f, single: e.target.checked }))} /> Giornata singola
                  </label>
                  {pnr.single ? (
                    <DatePicker label="Data" value={pnr.start_date} onChange={(v) => setPnr((f) => ({ ...f, start_date: v }))} testid="pnr-start-date" />
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <DatePicker label="Dal" value={pnr.start_date} onChange={(v) => setPnr((f) => ({ ...f, start_date: v }))} testid="pnr-start-date" />
                      <DatePicker label="Al" value={pnr.end_date} onChange={(v) => setPnr((f) => ({ ...f, end_date: v }))} testid="pnr-end-date" />
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <DatePicker label="Data" value={pnr.start_date} onChange={(v) => setPnr((f) => ({ ...f, start_date: v }))} testid="pnr-start-date" />
                  <div className="grid grid-cols-2 gap-3">
                    <TimePicker label="Dalle ore" value={pnr.start_time} onChange={(v) => setPnr((f) => ({ ...f, start_time: v }))} testid="pnr-start-time" />
                    <TimePicker label="Alle ore" value={pnr.end_time} onChange={(v) => setPnr((f) => ({ ...f, end_time: v }))} testid="pnr-end-time" />
                  </div>
                </>
              )}
              <p className="text-xs text-slate-500" data-testid="pnr-hint">
                {pnr.kind === "full" ? "Giornata/e intera/e: nel Libro Presenze verrà mostrata la sigla PNR, senza attribuzione automatica di ore." : "Le ore verranno calcolate automaticamente dalla fascia oraria."}
              </p>
              <div className="space-y-1.5">
                <Label>Nota <span className="text-destructive">*</span></Label>
                <Textarea data-testid="pnr-note-input" value={pnr.note} onChange={(e) => setPnr((f) => ({ ...f, note: e.target.value }))} rows={3} placeholder="Indica la motivazione della richiesta (obbligatoria)" />
              </div>
              {error && <p className="text-sm text-destructive" data-testid="leave-error">{error}</p>}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setMode(null)}>Annulla</Button>
                <Button type="submit" disabled={saving || !pnr.note.trim()} data-testid="pnr-submit-btn">{saving ? "Invio…" : "Invia richiesta"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Assemblea sindacale */}
        <Dialog open={mode === "assemblea"} onOpenChange={(o) => !o && setMode(null)}>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle className="font-heading">Richiesta Assemblea sindacale</DialogTitle></DialogHeader>
            <form onSubmit={submitAssemblea} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Appalto</Label>
                <Select value={assemblea.appalto_id} onValueChange={(v) => setAssemblea((f) => ({ ...f, appalto_id: v }))}>
                  <SelectTrigger data-testid="assemblea-appalto-select" className="h-12"><SelectValue placeholder="Seleziona l'appalto" /></SelectTrigger>
                  <SelectContent>{appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="assemblea-kind-hours" onClick={() => setAssemblea((f) => ({ ...f, kind: "hours" }))}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${assemblea.kind === "hours" ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-600"}`}>A ore (dalle / alle)</button>
                <button type="button" data-testid="assemblea-kind-full" onClick={() => setAssemblea((f) => ({ ...f, kind: "full" }))}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${assemblea.kind === "full" ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-600"}`}>Giornata intera</button>
              </div>
              {assemblea.kind === "hours" ? (
                <>
                  <DatePicker label="Data" value={assemblea.start_date} onChange={(v) => setAssemblea((f) => ({ ...f, start_date: v }))} testid="assemblea-start-date" />
                  <div className="grid grid-cols-2 gap-3">
                    <TimePicker label="Dalle ore" value={assemblea.start_time} onChange={(v) => setAssemblea((f) => ({ ...f, start_time: v }))} testid="assemblea-start-time" />
                    <TimePicker label="Alle ore" value={assemblea.end_time} onChange={(v) => setAssemblea((f) => ({ ...f, end_time: v }))} testid="assemblea-end-time" />
                  </div>
                </>
              ) : (
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer" data-testid="assemblea-single-toggle">
                    <input type="checkbox" checked={assemblea.single} onChange={(e) => setAssemblea((f) => ({ ...f, single: e.target.checked }))} /> Giornata singola
                  </label>
                  {assemblea.single ? (
                    <DatePicker label="Data" value={assemblea.start_date} onChange={(v) => setAssemblea((f) => ({ ...f, start_date: v }))} testid="assemblea-start-date" />
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <DatePicker label="Dal" value={assemblea.start_date} onChange={(v) => setAssemblea((f) => ({ ...f, start_date: v }))} testid="assemblea-start-date" />
                      <DatePicker label="Al" value={assemblea.end_date} onChange={(v) => setAssemblea((f) => ({ ...f, end_date: v }))} testid="assemblea-end-date" />
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-slate-500" data-testid="assemblea-hint">
                {assemblea.kind === "hours" ? "Le ore verranno calcolate automaticamente dalla fascia oraria." : "Giornata/e intera/e: nel Libro Presenze verrà mostrata la sigla AS, senza attribuzione automatica di ore."}
              </p>
              <div className="space-y-1.5"><Label>Note (opzionale)</Label><Textarea data-testid="assemblea-note-input" value={assemblea.note} onChange={(e) => setAssemblea((f) => ({ ...f, note: e.target.value }))} rows={3} /></div>
              {error && <p className="text-sm text-destructive" data-testid="leave-error">{error}</p>}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setMode(null)}>Annulla</Button>
                <Button type="submit" disabled={saving} data-testid="assemblea-submit-btn">{saving ? "Invio…" : "Invia richiesta"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog dichiarazione Permesso sindacale RSU/RSA (una tantum) */}
        <Dialog open={mode === "sind-confirm"} onOpenChange={(o) => !o && setMode(null)}>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle className="font-heading">Prima di utilizzare il Permesso sindacale RSU/RSA</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="rounded-xl bg-[#E8EFF0] border border-[#BCCCD0] px-4 py-3 flex items-start gap-2.5" data-testid="sind-confirm-text">
                <ShieldCheck className="h-5 w-5 text-[#22364D] mt-0.5 shrink-0" />
                <p className="text-sm text-slate-700 leading-snug">Dichiaro di essere componente di una RSU/RSA e di richiedere il permesso nell'ambito delle mie funzioni di rappresentanza sindacale.</p>
              </div>
              {error && <p className="text-sm text-destructive" data-testid="leave-error">{error}</p>}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setMode(null)}>Annulla</Button>
                <Button type="button" disabled={saving} onClick={confirmSind} data-testid="sind-confirm-btn" className="bg-[#3E6470] hover:bg-[#35555f]">
                  {saving ? "Attendere…" : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Confermo</>}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog Permesso sindacale RSU/RSA */}
        <Dialog open={mode === "sind"} onOpenChange={(o) => !o && setMode(null)}>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle className="font-heading">Richiesta Permesso sindacale RSU/RSA</DialogTitle></DialogHeader>
            <form onSubmit={submitSind} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Appalto</Label>
                <Select value={sind.appalto_id} onValueChange={(v) => setSind((f) => ({ ...f, appalto_id: v }))}>
                  <SelectTrigger data-testid="sind-appalto-select" className="h-12"><SelectValue placeholder="Seleziona l'appalto" /></SelectTrigger>
                  <SelectContent>{appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="sind-kind-full" onClick={() => setSind((f) => ({ ...f, kind: "full" }))}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${sind.kind === "full" ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-600"}`}>Giornata intera</button>
                <button type="button" data-testid="sind-kind-hours" onClick={() => setSind((f) => ({ ...f, kind: "hours" }))}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${sind.kind === "hours" ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-600"}`}>A ore (dalle / alle)</button>
              </div>
              {sind.kind === "full" ? (
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer" data-testid="sind-single-toggle">
                    <input type="checkbox" checked={sind.single} onChange={(e) => setSind((f) => ({ ...f, single: e.target.checked }))} /> Giornata singola
                  </label>
                  {sind.single ? (
                    <DatePicker label="Data" value={sind.start_date} onChange={(v) => setSind((f) => ({ ...f, start_date: v }))} testid="sind-start-date" />
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <DatePicker label="Dal" value={sind.start_date} onChange={(v) => setSind((f) => ({ ...f, start_date: v }))} testid="sind-start-date" />
                      <DatePicker label="Al" value={sind.end_date} onChange={(v) => setSind((f) => ({ ...f, end_date: v }))} testid="sind-end-date" />
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <DatePicker label="Data" value={sind.start_date} onChange={(v) => setSind((f) => ({ ...f, start_date: v }))} testid="sind-start-date" />
                  <div className="grid grid-cols-2 gap-3">
                    <TimePicker label="Dalle ore" value={sind.start_time} onChange={(v) => setSind((f) => ({ ...f, start_time: v }))} testid="sind-start-time" />
                    <TimePicker label="Alle ore" value={sind.end_time} onChange={(v) => setSind((f) => ({ ...f, end_time: v }))} testid="sind-end-time" />
                  </div>
                </>
              )}
              <p className="text-xs text-slate-500" data-testid="sind-preavviso-note">
                Il permesso sindacale deve essere comunicato, di regola, con almeno 24 ore di anticipo.
              </p>
              <p className="text-xs text-slate-500" data-testid="sind-hint">
                {sind.kind === "full" ? "Giornata/e intera/e: nel Libro Presenze verrà mostrata la sigla RSU, senza attribuzione automatica di ore." : "Le ore verranno calcolate automaticamente dalla fascia oraria."}
              </p>
              <div className="space-y-1.5"><Label>Note (opzionale)</Label><Textarea data-testid="sind-note-input" value={sind.note} onChange={(e) => setSind((f) => ({ ...f, note: e.target.value }))} rows={3} /></div>
              {error && <p className="text-sm text-destructive" data-testid="leave-error">{error}</p>}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setMode(null)}>Annulla</Button>
                <Button type="submit" disabled={saving} data-testid="sind-submit-btn">{saving ? "Invio…" : "Invia richiesta"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog conferma Diritto allo studio (una tantum) */}
        <Dialog open={mode === "ds-confirm"} onOpenChange={(o) => !o && setMode(null)}>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle className="font-heading">Prima di utilizzare il Diritto allo studio</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="rounded-xl bg-[#E8EEF6] border border-[#B9CBDD] px-4 py-3 flex items-start gap-2.5" data-testid="ds-confirm-text">
                <ShieldCheck className="h-5 w-5 text-[#22364D] mt-0.5 shrink-0" />
                <p className="text-sm text-slate-700 leading-snug">Confermo di aver già consegnato all'Ufficio Personale la documentazione necessaria per usufruire del permesso Diritto allo studio.</p>
              </div>
              {error && <p className="text-sm text-destructive" data-testid="leave-error">{error}</p>}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setMode(null)}>Annulla</Button>
                <Button type="button" disabled={saving} onClick={confirmDS} data-testid="ds-confirm-btn" className="bg-[#2F5C7D] hover:bg-[#264c68]">
                  {saving ? "Attendere…" : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Confermo</>}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog Diritto allo studio */}
        <Dialog open={mode === "ds"} onOpenChange={(o) => !o && setMode(null)}>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle className="font-heading">Richiesta Diritto allo studio</DialogTitle></DialogHeader>
            <form onSubmit={submitDS} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Appalto</Label>
                <Select value={ds.appalto_id} onValueChange={(v) => setDS((f) => ({ ...f, appalto_id: v }))}>
                  <SelectTrigger data-testid="ds-appalto-select" className="h-12"><SelectValue placeholder="Seleziona l'appalto" /></SelectTrigger>
                  <SelectContent>{appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="ds-kind-full" onClick={() => setDS((f) => ({ ...f, kind: "full" }))}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${ds.kind === "full" ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-600"}`}>Giornata intera</button>
                <button type="button" data-testid="ds-kind-hours" onClick={() => setDS((f) => ({ ...f, kind: "hours" }))}
                  className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${ds.kind === "hours" ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-600"}`}>A ore (dalle / alle)</button>
              </div>
              {ds.kind === "full" ? (
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer" data-testid="ds-single-toggle">
                    <input type="checkbox" checked={ds.single} onChange={(e) => setDS((f) => ({ ...f, single: e.target.checked }))} /> Giornata singola
                  </label>
                  {ds.single ? (
                    <DatePicker label="Data" value={ds.start_date} onChange={(v) => setDS((f) => ({ ...f, start_date: v }))} testid="ds-start-date" />
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <DatePicker label="Dal" value={ds.start_date} onChange={(v) => setDS((f) => ({ ...f, start_date: v }))} testid="ds-start-date" />
                      <DatePicker label="Al" value={ds.end_date} onChange={(v) => setDS((f) => ({ ...f, end_date: v }))} testid="ds-end-date" />
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <DatePicker label="Data" value={ds.start_date} onChange={(v) => setDS((f) => ({ ...f, start_date: v }))} testid="ds-start-date" />
                  <div className="grid grid-cols-2 gap-3">
                    <TimePicker label="Dalle ore" value={ds.start_time} onChange={(v) => setDS((f) => ({ ...f, start_time: v }))} testid="ds-start-time" />
                    <TimePicker label="Alle ore" value={ds.end_time} onChange={(v) => setDS((f) => ({ ...f, end_time: v }))} testid="ds-end-time" />
                  </div>
                </>
              )}
              <div className="space-y-1.5">
                <Label>Giustificativo universitario / attestazione (opzionale)</Label>
                <label className="flex items-center gap-2 rounded-xl border border-dashed border-slate-300 px-3 py-2.5 cursor-pointer hover:bg-slate-50" data-testid="ds-file-label">
                  <Paperclip className="h-4 w-4 text-slate-500" />
                  <span className="text-sm text-slate-600 truncate">{dsFile ? dsFile.name : "Allega PDF o immagine (facoltativo)"}</span>
                  <input type="file" accept=".pdf,image/*" className="hidden" data-testid="ds-file-input" onChange={(e) => setDsFile(e.target.files?.[0] || null)} />
                </label>
                <p className="text-xs text-slate-500">Puoi allegarlo ora oppure caricarlo successivamente da "Le tue richieste" (entro fine mese).</p>
              </div>
              <div className="space-y-1.5"><Label>Note (opzionale)</Label><Textarea data-testid="ds-note-input" value={ds.note} onChange={(e) => setDS((f) => ({ ...f, note: e.target.value }))} rows={3} /></div>
              {error && <p className="text-sm text-destructive" data-testid="leave-error">{error}</p>}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setMode(null)}>Annulla</Button>
                <Button type="submit" disabled={saving} data-testid="ds-submit-btn">{saving ? "Invio…" : "Invia richiesta"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {["lutto", "elett", "congedo"].map((k) => {
          const isL = k === "lutto"; const isC = k === "congedo";
          const form = isC ? congedo : isL ? lutto : elett; const setForm = isC ? setCongedo : isL ? setLutto : setElett;
          const file = isC ? congedoFile : isL ? luttoFile : elettFile; const setFile = isC ? setCongedoFile : isL ? setLuttoFile : setElettFile;
          const causale = isC ? "congedo" : isL ? "permesso_lutto" : "permesso_elettorale";
          const title = isC ? "Richiesta Congedo" : isL ? "Richiesta Permesso per lutto" : "Richiesta Permesso per funzioni elettorali";
          const docName = isC ? "Documentazione del congedo" : isL ? "Certificazione del decesso / dichiarazione sostitutiva" : "Attestazione di presenza al seggio";
          const msg = isC ? "Richiesta Congedo inviata" : isL ? "Richiesta Permesso per lutto inviata" : "Richiesta Permesso per funzioni elettorali inviata";
          return (
            <Dialog key={k} open={mode === k} onOpenChange={(o) => !o && setMode(null)}>
              <DialogContent className="rounded-2xl">
                <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle></DialogHeader>
                <form onSubmit={(e) => submitSpec(e, causale, form, file, msg)} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>Appalto</Label>
                    <Select value={form.appalto_id} onValueChange={(v) => setForm((f) => ({ ...f, appalto_id: v }))}>
                      <SelectTrigger data-testid={`${k}-appalto-select`} className="h-12"><SelectValue placeholder="Seleziona l'appalto" /></SelectTrigger>
                      <SelectContent>{appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" data-testid={`${k}-kind-full`} onClick={() => setForm((f) => ({ ...f, kind: "full" }))}
                      className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${form.kind === "full" ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-600"}`}>Giornata intera</button>
                    <button type="button" data-testid={`${k}-kind-hours`} onClick={() => setForm((f) => ({ ...f, kind: "hours" }))}
                      className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${form.kind === "hours" ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-600"}`}>A ore (dalle / alle)</button>
                  </div>
                  {form.kind === "full" ? (
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer" data-testid={`${k}-single-toggle`}>
                        <input type="checkbox" checked={form.single} onChange={(e) => setForm((f) => ({ ...f, single: e.target.checked }))} /> Giornata singola
                      </label>
                      {form.single ? (
                        <DatePicker label="Data" value={form.start_date} onChange={(v) => setForm((f) => ({ ...f, start_date: v }))} testid={`${k}-start-date`} />
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          <DatePicker label="Dal" value={form.start_date} onChange={(v) => setForm((f) => ({ ...f, start_date: v }))} testid={`${k}-start-date`} />
                          <DatePicker label="Al" value={form.end_date} onChange={(v) => setForm((f) => ({ ...f, end_date: v }))} testid={`${k}-end-date`} />
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <DatePicker label="Data" value={form.start_date} onChange={(v) => setForm((f) => ({ ...f, start_date: v }))} testid={`${k}-start-date`} />
                      <div className="grid grid-cols-2 gap-3">
                        <TimePicker label="Dalle ore" value={form.start_time} onChange={(v) => setForm((f) => ({ ...f, start_time: v }))} testid={`${k}-start-time`} />
                        <TimePicker label="Alle ore" value={form.end_time} onChange={(v) => setForm((f) => ({ ...f, end_time: v }))} testid={`${k}-end-time`} />
                      </div>
                    </>
                  )}
                  <div className="space-y-1.5">
                    <Label>{docName} {isC ? "(obbligatoria)" : "(opzionale)"}</Label>
                    <label className="flex items-center gap-2 rounded-xl border border-dashed border-slate-300 px-3 py-2.5 cursor-pointer hover:bg-slate-50" data-testid={`${k}-file-label`}>
                      <Paperclip className="h-4 w-4 text-slate-500" />
                      <span className="text-sm text-slate-600 truncate">{file ? file.name : isC ? "Allega PDF o immagine" : "Allega PDF o immagine (facoltativo)"}</span>
                      <input type="file" accept=".pdf,image/*" className="hidden" data-testid={`${k}-file-input`} onChange={(e) => setFile(e.target.files?.[0] || null)} />
                    </label>
                    <p className="text-xs text-slate-500">Puoi allegarlo ora oppure caricarlo successivamente da "Le tue richieste" (entro fine mese).</p>
                  </div>
                  <div className="space-y-1.5"><Label>{isC ? "Nota (facoltativa)" : "Note (opzionale)"}</Label><Textarea data-testid={`${k}-note-input`} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} rows={3} /></div>
                  {error && <p className="text-sm text-destructive" data-testid="leave-error">{error}</p>}
                  <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => setMode(null)}>Annulla</Button>
                    <Button type="submit" disabled={saving} data-testid={`${k}-submit-btn`}>{saving ? "Invio…" : "Invia richiesta"}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          );
        })}
      </main>
    </div>
  );
}